import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { GetAccountDetailResponse } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useMarkEmailVerified, useResendEmailVerification } from '../api/useAdmin.ts';
import { useToast, coerceErrorMessage } from './Toast.tsx';
import { useT } from '../i18n/index.ts';
import { Button, Chip, FormField, Input, Panel, buttonClass } from '../ui/index.ts';

/**
 * Admin-only email verification support card (resend + mark-verified).
 *
 * Extracted from `pages/AccountDetail.tsx` for the Customer 360 merge
 * (#861 PR A) so the 360 page can host the same interaction. The inline
 * AccountDetail copy is removed in PR B when that page becomes a
 * resolving redirect.
 *
 * Mark-verified requires a written reason — it lands in the audit row so
 * a support override is always explainable after the fact.
 */

interface EmailVerificationCardProps {
  readonly account: GetAccountDetailResponse;
}

export function EmailVerificationCard({ account }: EmailVerificationCardProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const resend = useResendEmailVerification();
  const markVerified = useMarkEmailVerified();
  const [reason, setReason] = useState('');
  const verified = account.email_verified === true;

  async function handleResend(): Promise<void> {
    try {
      await resend.mutateAsync({
        accountId: account.account_id,
        body: { reason: 'support resend' },
      });
      toast.success(t('account_detail.email_verification.toast.resent'));
    } catch (err) {
      toast.error(err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err));
    }
  }

  async function handleMarkVerified(): Promise<void> {
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      toast.error(t('account_detail.email_verification.error.reason_required'));
      return;
    }
    try {
      await markVerified.mutateAsync({
        accountId: account.account_id,
        body: { reason: trimmed },
      });
      setReason('');
      toast.success(t('account_detail.email_verification.toast.marked'));
    } catch (err) {
      toast.error(err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err));
    }
  }

  return (
    <Panel
      caption={t('account_detail.email_verification.title')}
      actions={
        <Chip tone={verified ? 'ok' : 'warn'} testId="account-email-verification-status">
          {verified
            ? t('account_detail.email_verification.status.verified')
            : t('account_detail.email_verification.status.unverified')}
        </Chip>
      }
      padded
      testId="account-email-verification-card"
    >
      <div className="flex flex-col gap-3">
        <p className="text-dense text-paper-low">{t('account_detail.email_verification.body')}</p>
        <div className="flex flex-wrap items-end gap-3">
          <Button
            onClick={() => void handleResend()}
            disabled={verified || markVerified.isPending}
            loading={resend.isPending}
            data-testid="account-email-verification-resend"
          >
            {resend.isPending
              ? t('account_detail.email_verification.resending')
              : t('account_detail.email_verification.resend')}
          </Button>
          <FormField
            label={t('account_detail.email_verification.reason_label')}
            fieldId="account-email-verification-reason"
            className="min-w-64 flex-1"
          >
            <Input
              id="account-email-verification-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              disabled={verified || resend.isPending || markVerified.isPending}
              placeholder={t('account_detail.email_verification.reason_placeholder')}
              data-testid="account-email-verification-reason"
            />
          </FormField>
          <Button
            variant="primary"
            onClick={() => void handleMarkVerified()}
            disabled={verified || resend.isPending}
            loading={markVerified.isPending}
            data-testid="account-email-verification-mark"
          >
            {markVerified.isPending
              ? t('account_detail.email_verification.marking')
              : t('account_detail.email_verification.mark_verified')}
          </Button>
          <Link
            to={`/customers/requests?accountId=${encodeURIComponent(account.account_id)}`}
            data-testid="account-support-requests-link"
            className={buttonClass('secondary')}
          >
            {t('account_detail.email_verification.support_link')}
          </Link>
        </div>
      </div>
    </Panel>
  );
}
